// Aggregator billing dashboard — Story #3501 Stream 5.3
import { useState, useEffect, useContext } from 'react'
import type { LucideIcon } from 'lucide-react'
import { CreditCard, Users, BarChart2, AlertCircle, ExternalLink, Loader2, TrendingUp, Building2 } from 'lucide-react'
import {
  getSubscriptionStatus,
  getBillingBalance,
  getCustomerPortal,
  updateAggregatorSeats,
  type SubscriptionStatus,
  type BalanceInfo,
  type AggregatorStatus,
} from '../../services/api'
import PageHeader from '../../components/ui/PageHeader'
import { AuthContext } from '../../contexts/AuthContext'
import { formatAud, usageMeterColor } from './billingUsage'

// UX-041: a failed plan request must never be shown as a plan. We keep the
// failure instead of defaulting the tier to 'trial'.
export interface BillingLoadFailure {
  /** The rejected value from the plan request, kept so the HTTP status can be read. */
  error: unknown
}

function statusOf(f: BillingLoadFailure): number | null {
  const status = (f.error as { response?: { status?: number } } | null)?.response?.status
  return typeof status === 'number' ? status : null
}

function billingFailureDetail(f: BillingLoadFailure): string {
  const status = statusOf(f)
  if (status === 403) return "Error 403: you don't manage billing for this account."
  if (status === 404) return "Error 404: we couldn't find a billing record for this account."
  if (status) return `Error ${status}: billing didn't respond as expected.`
  return "We couldn't reach billing."
}

// A seat-holder is a member of a named organisation whose billing someone else pays.
// Personal workspaces have no human name (a raw id), so they never count as one.
const RAW_ID = /^(personal-)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
function namedOrg(name?: string | null): string | null {
  const n = (name || '').trim()
  // A personal account's "organisation" is sometimes the user's own email address.
  return n && !RAW_ID.test(n) && !n.includes('@') ? n : null
}

export function BillingLoadError({
  failure,
  orgName,
  onRetry,
}: {
  failure: BillingLoadFailure
  orgName?: string | null
  onRetry: () => void
}) {
  const org = statusOf(failure) === 403 ? namedOrg(orgName) : null
  if (org) {
    return (
      <div role="status" className="rounded-xl border border-gray-200 bg-white p-5 mb-4">
        <p className="font-semibold text-gray-900">Billing is managed by {org}</p>
        <p className="text-sm text-gray-600 mt-1">
          You&apos;re on a seat in this organisation, so you don&apos;t pay directly.
        </p>
      </div>
    )
  }
  return (
    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 mb-4 text-sm text-red-800">
      <p className="font-semibold">We couldn&apos;t load your plan.</p>
      <p className="mt-0.5">{billingFailureDetail(failure)}</p>
      <p className="mt-2 flex items-center gap-3">
        <button onClick={onRetry} className="font-semibold underline">Try again</button>
        <a href="/support?report=1" className="underline">Contact support</a>
      </p>
    </div>
  )
}

const TIER_LABELS: Record<string, string> = {
  aggregator_starter: 'Aggregator Starter',
  aggregator_growth: 'Aggregator Growth',
  aggregator_enterprise: 'Aggregator Enterprise',
  trial: 'Trial', basic: 'Basic', inventor: 'Inventor',
  inventor_free: 'Inventor (Grandfathered)', pro_inventor: 'Pro Inventor',
  unlimited: 'Unlimited',
}

const TIER_COLORS: Record<string, string> = {
  aggregator_starter: 'bg-sky-100 text-sky-800',
  aggregator_growth: 'bg-blue-100 text-blue-800',
  aggregator_enterprise: 'bg-purple-100 text-purple-800',
  trial: 'bg-gray-100 text-gray-700',
  basic: 'bg-sky-100 text-sky-700',
  inventor: 'bg-blue-100 text-blue-700',
  inventor_free: 'bg-emerald-100 text-emerald-700',
  pro_inventor: 'bg-purple-100 text-purple-700',
  unlimited: 'bg-emerald-100 text-emerald-700',
}

const TIER_MAX_ENTITIES: Record<string, number | null> = {
  aggregator_starter: 50,
  aggregator_growth: 200,
  aggregator_enterprise: null,
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    past_due: 'bg-yellow-100 text-yellow-700',
    cancelled: 'bg-red-100 text-red-700',
    trialing: 'bg-sky-100 text-sky-700',
    pending: 'bg-gray-100 text-gray-700',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colors[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {status.replace('_', ' ')}
    </span>
  )
}

function StatCard({ label, value, sub, icon: Icon, color = 'sky' }: {
  label: string; value: string | number; sub?: string
  icon: LucideIcon
  color?: 'sky' | 'blue' | 'purple' | 'emerald' | 'amber'
}) {
  const bg: Record<string, string> = {
    sky: 'bg-sky-50 border-sky-100', blue: 'bg-blue-50 border-blue-100',
    purple: 'bg-purple-50 border-purple-100', emerald: 'bg-emerald-50 border-emerald-100',
    amber: 'bg-amber-50 border-amber-100',
  }
  const ic: Record<string, string> = {
    sky: 'text-sky-500', blue: 'text-blue-500', purple: 'text-purple-500',
    emerald: 'text-emerald-500', amber: 'text-amber-500',
  }
  return (
    <div className={`rounded-xl border p-5 ${bg[color]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
        </div>
        <Icon size={22} className={ic[color]} />
      </div>
    </div>
  )
}

function SeatManager({ aggregatorId, currentCount, maxEntities }: {
  aggregatorId: string; currentCount: number; maxEntities: number | null; tier: string
}) {
  const [entityId, setEntityId] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [localCount, setLocalCount] = useState(currentCount)

  const addEntity = async () => {
    if (!entityId.trim()) return
    setSaving(true)
    setError('')
    try {
      const res = await updateAggregatorSeats({ aggregator_id: aggregatorId, entity_id: entityId.trim(), delta: 1 })
      setLocalCount(res.entity_count)
      setEntityId('')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to add entity'
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  const pct = maxEntities ? Math.round((localCount / maxEntities) * 100) : 0
  const atCap = maxEntities !== null && localCount >= maxEntities

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-4">
        <Users size={18} className="text-blue-500" />
        <h3 className="font-semibold text-gray-900">Entity Seats</h3>
        <span className="ml-auto text-sm text-gray-500">
          {localCount}{maxEntities ? ` / ${maxEntities}` : ' (unlimited)'}
        </span>
      </div>

      {maxEntities && (
        <div className="mb-4">
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${atCap ? 'bg-red-400' : pct > 80 ? 'bg-amber-400' : 'bg-blue-400'}`}
              style={{ width: `${Math.min(pct, 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">{pct}% of seat allowance used</p>
        </div>
      )}

      {atCap && (
        <div className="mb-4 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          <AlertCircle size={14} />
          Seat cap reached — upgrade to add more entities
        </div>
      )}

      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Entity ID or email"
          value={entityId}
          onChange={(e) => setEntityId(e.target.value)}
          disabled={atCap || saving}
          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
        />
        <button
          onClick={addEntity}
          disabled={!entityId.trim() || atCap || saving}
          className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
        >
          {saving && <Loader2 size={14} className="animate-spin" />}
          Add
        </button>
      </div>
      {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
    </div>
  )
}

export default function BillingDashboard() {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null)
  const [balance, setBalance] = useState<BalanceInfo | null>(null)
  const [aggregator, setAggregator] = useState<AggregatorStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [portalLoading, setPortalLoading] = useState(false)
  const [planFailure, setPlanFailure] = useState<BillingLoadFailure | null>(null)
  const [balanceFailed, setBalanceFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const auth = useContext(AuthContext)
  const activeOrg = auth?.user?.org_memberships?.find((m) => m.org_id === auth.user?.active_org_id)
    || auth?.user?.org_memberships?.[0]

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setPlanFailure(null)
      setBalanceFailed(false)
      try {
        const [sub, bal] = await Promise.all([
          getSubscriptionStatus().catch((e) => { setPlanFailure({ error: e }); return null }),
          getBillingBalance().catch(() => { setBalanceFailed(true); return null }),
        ])
        if (sub) {
          setSubscription(sub)
          if (sub.aggregator_id) {
            setAggregator({
              aggregator_id: sub.aggregator_id,
              entity_count: sub.entity_count ?? 0,
              tier: sub.tier,
            })
          }
        }
        if (bal) setBalance(bal)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [attempt])

  const [stripeError, setStripeError] = useState('')

  const handleManageSubscription = async () => {
    setPortalLoading(true)
    setStripeError('')
    try {
      const { portal_url } = await getCustomerPortal()
      if (portal_url) window.open(portal_url, '_blank', 'noopener,noreferrer')
      else setStripeError('Stripe portal not configured in this environment.')
    } catch {
      setStripeError('Unable to open Stripe portal. Please try again or contact support.')
    } finally {
      setPortalLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 size={28} className="animate-spin text-blue-400" />
      </div>
    )
  }

  if (planFailure && !subscription) {
    return (
      <div className="max-w-3xl mx-auto">
        <PageHeader
          title="Billing"
          description="Your plan, usage and credits."
          helpKey="account-billing"
        />
        <BillingLoadError
          failure={planFailure}
          orgName={activeOrg?.org_name || auth?.user?.organization}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </div>
    )
  }

  const tier = subscription?.tier ?? 'trial'
  const isAggregator = tier.startsWith('aggregator_')
  const maxEntities = TIER_MAX_ENTITIES[tier] ?? null
  const usedUsd = parseFloat(balance?.used_usd ?? '0')
  // The billing API uses a numeric -1 as the "unlimited" sentinel for both the
  // remaining balance and the monthly credit (TIER_CREDITS['unlimited'] === -1).
  // Rendering it verbatim produced "$-1.00 available credit". Show "Unlimited".
  const remainingUnlimited = Number(balance?.remaining_usd) === -1
  const monthlyUnlimited = Number(balance?.monthly_credit_usd) === -1
  const remainingUsd = remainingUnlimited ? 0 : parseFloat(balance?.remaining_usd ?? '0')
  const monthlyUsd = monthlyUnlimited ? 0 : parseFloat(balance?.monthly_credit_usd ?? '0')

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title="Billing"
        description="Your plan, usage and credits. Cancellations, payment methods and invoices are managed securely in the Stripe customer portal."
        helpKey="account-billing"
        primaryAction={
          <button
            onClick={handleManageSubscription}
            disabled={portalLoading}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50"
          >
            {portalLoading ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
            Manage in Stripe
          </button>
        }
      />
      {stripeError && <p className="-mt-4 mb-4 text-xs text-red-500">{stripeError}</p>}

      {/* Tier + status row */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 mb-4 flex items-center gap-4">
        <CreditCard size={22} className="text-blue-500 shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-gray-500">Current plan</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`px-2.5 py-0.5 rounded-full text-sm font-semibold ${TIER_COLORS[tier] ?? 'bg-gray-100 text-gray-700'}`}>
              {TIER_LABELS[tier] ?? tier}
            </span>
            {subscription?.subscription_status && (
              <StatusBadge status={subscription.subscription_status} />
            )}
          </div>
        </div>
        {subscription?.trial_expires_at && (
          <p className="text-xs text-gray-500 shrink-0">
            Trial expires {new Date(subscription.trial_expires_at).toLocaleDateString()}
          </p>
        )}
      </div>

      {balanceFailed && (
        <p role="alert" className="-mt-2 mb-4 text-xs text-red-600">
          We couldn&apos;t load your credit balance, so no amounts are shown.{' '}
          <button onClick={() => setAttempt((n) => n + 1)} className="font-semibold underline">Try again</button>
        </p>
      )}

      {/* Stats grid — aggregator only */}
      {isAggregator && (
        <div className="grid gap-4 mb-4 grid-cols-2 sm:grid-cols-4">
          {aggregator && (
            <StatCard
              label="Entities"
              value={aggregator.entity_count}
              sub={maxEntities ? `of ${maxEntities} seats` : 'unlimited'}
              icon={Building2}
              color="blue"
            />
          )}
          <StatCard
            label="Monthly budget"
            value={balanceFailed ? 'Unavailable' : monthlyUnlimited ? 'Unlimited' : formatAud(monthlyUsd)}
            sub="Per billing cycle"
            icon={CreditCard}
            color="sky"
          />
          <StatCard
            label="Used this cycle"
            value={balanceFailed ? 'Unavailable' : formatAud(usedUsd)}
            sub="AI usage"
            icon={TrendingUp}
            color={usageMeterColor(usedUsd, monthlyUsd, monthlyUnlimited)}
          />
          <StatCard
            label="Remaining"
            value={balanceFailed ? 'Unavailable' : remainingUnlimited ? 'Unlimited' : formatAud(remainingUsd)}
            sub="Available credit"
            icon={BarChart2}
            color="emerald"
          />
        </div>
      )}

      {/* Seat manager — aggregator only */}
      {isAggregator && aggregator && (
        <SeatManager
          aggregatorId={aggregator.aggregator_id}
          currentCount={aggregator.entity_count}
          maxEntities={maxEntities}
          tier={tier}
        />
      )}

      {/* Non-aggregator upgrade nudge */}
      {!isAggregator && tier !== 'unlimited' && (
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-5 flex items-start gap-3">
          <Users size={20} className="text-blue-500 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-blue-900">Running a portfolio or accelerator?</p>
            <p className="text-sm text-blue-700 mt-1">
              Aggregator plans let you manage inventor entities at scale with per-entity billing,
              cross-reveal consents, and AI usage budgets.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
